import type { CalendarDate, CalendarDateTime, Time } from '@internationalized/date';
import type { DatetimeFieldOptions, PrimitiveType, SegmentedFieldFilter } from '#layers/data-model/public';
import type { RangeValueAdapter } from '#layers/ui/public';
import { Temporal } from 'temporal-polyfill';
import {
	calendarDateConverter,
	calendarDateTimeConverter,
	formatPlainDate,
	isZonedDateTime,
	nowDate,
	nowDateTime,
	nowInTimezone,
	resolveTimezone,
	shortPlainDateFormatOptions,
	timeConverter,
	TIMEZONE_LOCAL,
	TIMEZONE_UTC,
	unixTimestampConverter,
} from '#layers/base/public';
import { isEpochIntegerPrimitiveTypeName, PRIMITIVE_TYPE_DATE, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_TIME, PRIMITIVE_TYPE_UINT64 } from '#layers/data-model/public';

/**
 * Canonical stored operand for a datetime filter. Date-like backings carry `Temporal` instances
 * (the fetch boundary rejects any non-canonical date-like operand); timestamp backings carry the
 * unix-millisecond epoch.
 *
 * - `date` -> `Temporal.PlainDate`
 * - `time` -> `Temporal.PlainTime`
 * - `dateTime` -> `Temporal.PlainDateTime`
 * - `dateTimeWithTimezone` -> `Temporal.ZonedDateTime`
 * - `int64` / `uint64` (timestamp-backed) -> `string` (unix milliseconds; 64-bit columns travel as
 *   strings to preserve precision beyond `Number.MAX_SAFE_INTEGER`)
 */
export type DatetimeFilterValue =
	| Temporal.PlainDate
	| Temporal.PlainTime
	| Temporal.PlainDateTime
	| Temporal.ZonedDateTime
	| string;

/** The datetime backing of a filter plus the timezone options needed to resolve it. */
export interface DatetimeFilterContext extends DatetimeFieldOptions {
	primitiveType: PrimitiveType;
}

export function buildDatetimeFilterContext(
	primitiveType: PrimitiveType,
	typeOptions?: DatetimeFieldOptions,
): DatetimeFilterContext {
	return { primitiveType, ...typeOptions };
}

/** Reads a `_between`/`_nbetween` operand into a `[start, end]` tuple, tolerating a missing/partial value. */
export function getDatetimeRangeModel(value: unknown): [unknown, unknown] {
	if (!Array.isArray(value)) return [undefined, undefined];
	return [value[0], value[1]];
}

/** Immutably sets one boundary of a range filter, preserving the other. */
export function setDatetimeRangeBoundary(
	model: SegmentedFieldFilter,
	index: 0 | 1,
	value: unknown,
): SegmentedFieldFilter {
	const range = getDatetimeRangeModel(model.value);
	range[index] = value;
	return { ...model, value: range };
}

/** Timezone-only backings store and display in UTC; `dateTimeWithTimezone` uses the resolved zone. */
const STORAGE_TIMEZONE = TIMEZONE_UTC;

export function isDateDatetimeFilter(primitiveType: PrimitiveType): boolean {
	return primitiveType.name === PRIMITIVE_TYPE_DATE;
}

export function isTimeDatetimeFilter(primitiveType: PrimitiveType): boolean {
	return primitiveType.name === PRIMITIVE_TYPE_TIME;
}

export function isTimestampBackedDatetimeFilter(primitiveType: PrimitiveType): boolean {
	return isEpochIntegerPrimitiveTypeName(primitiveType.name);
}

/** A uint64 epoch cannot represent an instant before 1970-01-01T00:00:00Z. */
export function isTimestampValueCompatibleWithBacking(value: unknown, primitiveType: PrimitiveType): boolean {
	if (primitiveType.name !== PRIMITIVE_TYPE_UINT64) return true;

	const epochMilliseconds = timestampOperandToEpochMilliseconds(value);
	return epochMilliseconds == null || epochMilliseconds >= 0;
}

/** Reads a timestamp operand (number or stringified epoch) back into a unix-millisecond number. */
function timestampOperandToEpochMilliseconds(value: unknown): number | null {
	const epochMilliseconds = typeof value === 'string' ? Number(value) : value;
	return typeof epochMilliseconds === 'number' && Number.isFinite(epochMilliseconds) ? epochMilliseconds : null;
}

export function isDateTimeWithTimezoneDatetimeFilter(primitiveType: PrimitiveType): boolean {
	return primitiveType.name === PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE;
}

export function resolveDatetimeFilterTimezone(options?: DatetimeFieldOptions): string {
	return resolveTimezone(options?.defaultTimezone?.trim() || TIMEZONE_LOCAL);
}

export function datetimeFilterValueToCalendarDate(value: unknown): CalendarDate | null {
	return value instanceof Temporal.PlainDate ? calendarDateConverter.toCalendar(value) : null;
}

export function calendarDateToDatetimeFilterValue(value?: CalendarDate | null): Temporal.PlainDate | null {
	return calendarDateConverter.toTemporal(value);
}

export function datetimeFilterValueToTime(value: unknown): Time | null {
	return value instanceof Temporal.PlainTime ? timeConverter.toCalendar(value) : null;
}

export function timeToDatetimeFilterValue(value?: Time | null): Temporal.PlainTime | null {
	return timeConverter.toTemporal(value);
}

export function datetimeFilterValueToCalendarDateTime(
	value: unknown,
	context: DatetimeFilterContext,
): CalendarDateTime | null {
	return calendarDateTimeConverter.toCalendar(datetimeFilterValueToPlainDateTime(value, context));
}

export function calendarDateTimeToDatetimeFilterValue(
	value: CalendarDateTime | null | undefined,
	context: DatetimeFilterContext,
): Temporal.PlainDateTime | Temporal.ZonedDateTime | string | null {
	return plainDateTimeToDatetimeFilterValue(calendarDateTimeConverter.toTemporal(value), context);
}

/**
 * The value's date part as a `CalendarDate`, for the date+time-bearing backings
 * (`dateTime`, `dateTimeWithTimezone`, timestamp). Null when the value carries no date.
 */
export function datetimeRangeDatePart(value: unknown, context: DatetimeFilterContext): CalendarDate | null {
	const dateTime = datetimeFilterValueToPlainDateTime(value, context);
	return dateTime ? calendarDateConverter.toCalendar(dateTime.toPlainDate()) : null;
}

/** Combines `date` with `previous`'s time (midnight when absent), back into the canonical operand. */
export function withDatetimeRangeDate(
	previous: unknown,
	date: CalendarDate,
	context: DatetimeFilterContext,
): DatetimeFilterValue {
	const time = datetimeFilterValueToPlainDateTime(previous, context)?.toPlainTime() ?? new Temporal.PlainTime(0, 0);
	const dateTime = calendarDateConverter.toTemporal(date)!.toPlainDateTime(time);
	return plainDateTimeToDatetimeFilterValue(dateTime, context)!;
}

/** The value's time part as a `Time` (for `DInputTime`). Null when the value carries no time. */
export function datetimeRangeTimePart(value: unknown, context: DatetimeFilterContext): Time | null {
	const dateTime = datetimeFilterValueToPlainDateTime(value, context);
	return dateTime ? timeConverter.toCalendar(dateTime.toPlainTime()) : null;
}

/** Combines `previous`'s date (today when absent) with `time` (midnight when null), back into the canonical operand. */
export function withDatetimeRangeTime(
	previous: unknown,
	time: Time | null,
	context: DatetimeFilterContext,
): DatetimeFilterValue {
	const date = datetimeFilterValueToPlainDateTime(previous, context)?.toPlainDate() ?? nowDate();
	const plainTime = timeConverter.toTemporal(time) ?? new Temporal.PlainTime(0, 0);
	const dateTime = date.toPlainDateTime(plainTime);
	return plainDateTimeToDatetimeFilterValue(dateTime, context)!;
}

/** Range picker adapter for the date+time-bearing backings, splitting the operand into date and time parts. */
export function createDatetimeRangeAdapter(context: DatetimeFilterContext): RangeValueAdapter<DatetimeFilterValue> {
	return {
		toCalendarDate: (value) => datetimeRangeDatePart(value, context),
		withDate: (previous, date) => withDatetimeRangeDate(previous, date, context),
		now: () => datetimeFilterNowValue(context),
		format: (value, locale) => formatDatetimeFilterValue(value, context, locale),
	};
}

export function formatDatetimeFilterValue(
	value: unknown,
	context: DatetimeFilterContext,
	locale?: string,
): string {
	if (isDateDatetimeFilter(context.primitiveType)) {
		return formatPlainDate(datetimeFilterValueToPlainDate(value), locale, shortPlainDateFormatOptions) ?? '';
	}

	if (isTimeDatetimeFilter(context.primitiveType)) {
		return value instanceof Temporal.PlainTime ? value.toString({ smallestUnit: 'minute' }) : '';
	}

	const dateTime = datetimeFilterValueToPlainDateTime(value, context);
	if (!dateTime) return '';

	const date = formatPlainDate(dateTime.toPlainDate(), locale, shortPlainDateFormatOptions);
	const time = dateTime.toPlainTime().toString({ smallestUnit: 'minute' });
	return date ? `${date}, ${time}` : time;
}

/** Canonical "now" at the precision visible in the editor, per field backing. */
export function datetimeFilterNowValue(context: DatetimeFilterContext): DatetimeFilterValue {
	if (isDateDatetimeFilter(context.primitiveType)) {
		return nowDate();
	}

	if (isTimeDatetimeFilter(context.primitiveType)) {
		return toMinutePrecision(Temporal.Now.plainTimeISO());
	}

	if (isDateTimeWithTimezoneDatetimeFilter(context.primitiveType)) {
		return toMinutePrecision(nowInTimezone(resolveDatetimeFilterTimezone(context)));
	}

	if (isTimestampBackedDatetimeFilter(context.primitiveType)) {
		const epochMilliseconds = toMinutePrecision(nowInTimezone(STORAGE_TIMEZONE)).toInstant().epochMilliseconds;
		// Epoch-integer columns hold values beyond `Number.MAX_SAFE_INTEGER`, so their operands travel as strings.
		return String(epochMilliseconds);
	}

	return toMinutePrecision(nowDateTime());
}

/** Reads a stored operand into a plain datetime for the calendar widget, by backing. */
function datetimeFilterValueToPlainDateTime(
	value: unknown,
	context: DatetimeFilterContext,
): Temporal.PlainDateTime | null {
	if (isTimestampBackedDatetimeFilter(context.primitiveType)) {
		const epochMilliseconds = timestampOperandToEpochMilliseconds(value);
		return epochMilliseconds == null ? null : unixTimestampConverter.toPlainDateTime(epochMilliseconds, STORAGE_TIMEZONE);
	}

	if (isZonedDateTime(value)) {
		return value.withTimeZone(resolveDatetimeFilterTimezone(context)).toPlainDateTime();
	}

	return value instanceof Temporal.PlainDateTime ? value : null;
}

/** Writes a calendar-widget datetime back into the canonical stored operand, by backing. */
function plainDateTimeToDatetimeFilterValue(
	value: Temporal.PlainDateTime | null,
	context: DatetimeFilterContext,
): Temporal.PlainDateTime | Temporal.ZonedDateTime | string | null {
	if (!value) return null;

	if (isTimestampBackedDatetimeFilter(context.primitiveType)) {
		const epochMilliseconds = unixTimestampConverter.fromPlainDateTime(value, STORAGE_TIMEZONE);
		return epochMilliseconds == null ? null : String(epochMilliseconds);
	}

	if (isDateTimeWithTimezoneDatetimeFilter(context.primitiveType)) {
		return value.toZonedDateTime(resolveDatetimeFilterTimezone(context));
	}

	return value;
}

function datetimeFilterValueToPlainDate(value: unknown): Temporal.PlainDate | null {
	return value instanceof Temporal.PlainDate ? value : null;
}

function toMinutePrecision<T extends Temporal.PlainTime | Temporal.PlainDateTime | Temporal.ZonedDateTime>(value: T): T {
	return value.round({ smallestUnit: 'minute', roundingMode: 'trunc' }) as T;
}
